/**
 * Environment-Aware Logger Utility
 *
 * Provides structured logging that automatically adjusts based on NODE_ENV:
 * - Development: Full verbose logging with all methods
 * - Production: Only errors and warnings
 * - Test: Minimal/no logging
 *
 * Security: NEVER logs passwords, tokens, credit cards, or other sensitive data
 *
 * Usage:
 *   import logger from 'utils/logger';
 *
 *   logger.debug('Loading component', { componentId: 123 });
 *   logger.info('User action completed', { action: 'submit' });
 *   logger.warn('Deprecated API usage', { endpoint: '/old-api' });
 *   logger.error('Request failed', error);
 *
 * @module utils/logger
 */

// Environment detection
const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';
const isTest = process.env.NODE_ENV === 'test';

/**
 * Environment flags for conditional logic
 */
export const env = {
  isDevelopment,
  isProduction,
  isTest,
};

/**
 * Type for log data (any JSON-serializable value)
 */
type LogData = any;

/**
 * Basic Logger - Core logging methods
 */
class Logger {
  /**
   * Debug logging - Development only
   * Use for verbose debugging information
   * @param message - Log message
   * @param data - Optional data to log
   */
  debug(message: string, data?: LogData): void {
    if (isDevelopment) {
      if (data !== undefined) {
        console.log(`🐛 ${message}`, data);
      } else {
        console.log(`🐛 ${message}`);
      }
    }
  }

  /**
   * Info logging - Important events
   * Use for significant application events
   * @param message - Log message
   * @param data - Optional data to log
   */
  info(message: string, data?: LogData): void {
    if (isDevelopment) {
      if (data !== undefined) {
        console.log(`ℹ️  ${message}`, data);
      } else {
        console.log(`ℹ️  ${message}`);
      }
    }
  }

  /**
   * Warning logging - Always enabled
   * Use for recoverable issues or deprecated usage
   * @param message - Warning message
   * @param data - Optional data to log
   */
  warn(message: string, data?: LogData): void {
    if (data !== undefined) {
      console.warn(`⚠️  ${message}`, data);
    } else {
      console.warn(`⚠️  ${message}`);
    }
  }

  /**
   * Error logging - Always enabled
   * Use for errors and exceptions
   * @param message - Error message
   * @param error - Error object or additional data
   */
  error(message: string, error?: Error | LogData): void {
    if (error !== undefined) {
      console.error(`❌ ${message}`, error);
    } else {
      console.error(`❌ ${message}`);
    }
  }

  /**
   * Success logging - Development only
   * Use for successful operations
   * @param message - Success message
   * @param data - Optional data to log
   */
  success(message: string, data?: LogData): void {
    if (isDevelopment) {
      if (data !== undefined) {
        console.log(`✅ ${message}`, data);
      } else {
        console.log(`✅ ${message}`);
      }
    }
  }

  /**
   * API Request Logger - Development only
   */
  api = {
    /**
     * Log API request
     * @param method - HTTP method
     * @param url - Request URL
     * @param data - Optional request data (sanitized)
     */
    request: (method: string, url: string, data?: LogData): void => {
      if (isDevelopment) {
        if (data !== undefined) {
          console.log(`🚀 API ${method.toUpperCase()} ${url}`, data);
        } else {
          console.log(`🚀 API ${method.toUpperCase()} ${url}`);
        }
      }
    },

    /**
     * Log API response
     * @param method - HTTP method
     * @param url - Request URL
     * @param status - Response status code
     * @param data - Optional response data (sanitized)
     */
    response: (method: string, url: string, status: number, data?: LogData): void => {
      if (isDevelopment) {
        const emoji = status >= 200 && status < 300 ? '✅' : '❌';
        if (data !== undefined) {
          console.log(`${emoji} API ${method.toUpperCase()} ${url} - ${status}`, data);
        } else {
          console.log(`${emoji} API ${method.toUpperCase()} ${url} - ${status}`);
        }
      }
    },

    /**
     * Log API error
     * @param method - HTTP method
     * @param url - Request URL
     * @param error - Error object
     */
    error: (method: string, url: string, error: Error | LogData): void => {
      if (isDevelopment) {
        console.error(`❌ API ${method.toUpperCase()} ${url} failed`, error);
      } else {
        // In production, only log that an API call failed (no details)
        console.error(`API ${method.toUpperCase()} failed`);
      }
    },
  };

  /**
   * Authentication Logger - Development only
   * SECURITY: NEVER logs passwords or tokens
   */
  auth = {
    /**
     * Log authentication attempt
     * @param username - Username or email (sanitized)
     */
    attempt: (username: string): void => {
      if (isDevelopment) {
        // Sanitize: show only first 3 chars for privacy
        const sanitized = username.length > 3 ? `${username.slice(0, 3)}***` : '***';
        console.log(`🔐 Auth attempt: ${sanitized}`);
      }
    },

    /**
     * Log successful authentication
     * @param username - Username or email (sanitized)
     */
    success: (username: string): void => {
      if (isDevelopment) {
        const sanitized = username.length > 3 ? `${username.slice(0, 3)}***` : '***';
        console.log(`✅ Auth success: ${sanitized}`);
      }
    },

    /**
     * Log failed authentication
     * @param username - Username or email (sanitized)
     * @param reason - Optional failure reason (no sensitive data)
     */
    failure: (username: string, reason?: string): void => {
      if (isDevelopment) {
        const sanitized = username.length > 3 ? `${username.slice(0, 3)}***` : '***';
        if (reason) {
          console.log(`❌ Auth failed: ${sanitized} - ${reason}`);
        } else {
          console.log(`❌ Auth failed: ${sanitized}`);
        }
      }
    },

    /**
     * Log logout
     * @param username - Username or email (sanitized)
     */
    logout: (username: string): void => {
      if (isDevelopment) {
        const sanitized = username.length > 3 ? `${username.slice(0, 3)}***` : '***';
        console.log(`🚪 Logout: ${sanitized}`);
      }
    },
  };

  /**
   * Redux Logger - Development only
   */
  redux = {
    /**
     * Log Redux action
     * @param type - Action type
     * @param payload - Optional action payload
     */
    action: (type: string, payload?: LogData): void => {
      if (isDevelopment) {
        if (payload !== undefined) {
          console.log(`⚡ Redux: ${type}`, payload);
        } else {
          console.log(`⚡ Redux: ${type}`);
        }
      }
    },

    /**
     * Log Redux state
     * @param description - State description
     * @param state - State data (sanitized)
     */
    state: (description: string, state: LogData): void => {
      if (isDevelopment) {
        console.log(`📦 Redux State - ${description}:`, state);
      }
    },
  };

  /**
   * Performance Logger - Development only
   */
  perf = {
    /**
     * Start performance timer
     * @param label - Timer label
     * @returns Start time for use with end()
     */
    start: (label: string): number => {
      if (isDevelopment) {
        console.log(`⏱️  Perf start: ${label}`);
      }
      return performance.now();
    },

    /**
     * End performance timer
     * @param label - Timer label (must match start label)
     * @param startTime - Start time from start() call
     */
    end: (label: string, startTime?: number): void => {
      if (isDevelopment) {
        if (startTime !== undefined) {
          const duration = performance.now() - startTime;
          console.log(`⏱️  Perf end: ${label} - ${duration.toFixed(2)}ms`);
        } else {
          console.log(`⏱️  Perf end: ${label}`);
        }
      }
    },
  };

  /**
   * Grouped logging - Development only
   * @param label - Group label
   * @param callback - Function to execute within the group
   */
  group(label: string, callback: () => void): void {
    if (isDevelopment) {
      console.group(label);
      try {
        callback();
      } finally {
        console.groupEnd();
      }
    } else {
      // In production, just execute the callback without grouping
      callback();
    }
  }

  /**
   * Assertion - Throws in development, warns in production
   * @param condition - Condition to assert
   * @param message - Assertion message
   */
  assert(condition: boolean, message: string): void {
    if (!condition) {
      if (isDevelopment) {
        throw new Error(`Assertion failed: ${message}`);
      } else {
        console.warn(`⚠️  Assertion failed: ${message}`);
      }
    }
  }

  /**
   * Execute code only in development
   * Useful for development-only side effects
   * @param callback - Function to execute in development only
   */
  devOnly(callback: () => void): void {
    if (isDevelopment) {
      callback();
    }
  }

  /**
   * Sanitize user data for logging
   * Removes sensitive fields before logging
   * @param data - Data to sanitize
   * @returns Sanitized data safe for logging
   */
  sanitize(data: any): any {
    if (!data || typeof data !== 'object') {
      return data;
    }

    const sanitized = { ...data };
    const sensitiveFields = [
      'password',
      'token',
      'accessToken',
      'refreshToken',
      'jwt',
      'apiKey',
      'secret',
      'creditCard',
      'ccv',
      'ssn',
    ];

    sensitiveFields.forEach((field) => {
      if (sanitized[field] !== undefined) {
        sanitized[field] = '[REDACTED]';
      }
    });

    return sanitized;
  }
}

// Export singleton instance
const logger = new Logger();
export default logger;

/**
 * Usage Examples:
 *
 * // Basic logging
 * logger.debug('Component mounted', { componentId: 123 });
 * logger.info('User action', { action: 'click' });
 * logger.warn('Deprecated feature used');
 * logger.error('Request failed', error);
 *
 * // API logging
 * logger.api.request('GET', '/api/users');
 * logger.api.response('GET', '/api/users', 200, { count: 10 });
 * logger.api.error('POST', '/api/login', error);
 *
 * // Auth logging (auto-sanitizes usernames)
 * logger.auth.attempt('user@example.com');
 * logger.auth.success('user@example.com');
 * logger.auth.failure('user@example.com', 'Invalid password');
 *
 * // Redux logging
 * logger.redux.action('USER_LOGIN', { userId: 123 });
 * logger.redux.state('After login', store.getState());
 *
 * // Performance logging
 * const start = logger.perf.start('API call');
 * await fetchData();
 * logger.perf.end('API call', start);
 *
 * // Grouped logging
 * logger.group('User Registration', () => {
 *   logger.debug('Step 1: Validate');
 *   logger.debug('Step 2: Create');
 *   logger.debug('Step 3: Notify');
 * });
 *
 * // Sanitize before logging
 * const userData = { username: 'john', password: 'secret123' };
 * logger.debug('User data', logger.sanitize(userData));
 * // Logs: { username: 'john', password: '[REDACTED]' }
 */
